import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

export default function VoucherSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", attempts: 0 }); // polling | paid | failed | timeout
  const [info, setInfo] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!sessionId) {
      setState({ phase: "failed" });
      return;
    }
    const MAX_ATTEMPTS = 8;
    const INTERVAL_MS = 2000;
    const poll = async (attempt) => {
      if (cancelled.current) return;
      if (attempt >= MAX_ATTEMPTS) { setState({ phase: "timeout" }); return; }
      try {
        const r = await api.get(`/vouchers/checkout/status/${sessionId}`);
        const data = r.data || {};
        setInfo(data);
        if (data.payment_status === "paid") { setState({ phase: "paid" }); return; }
        if (data.status === "expired") { setState({ phase: "failed" }); return; }
        setTimeout(() => poll(attempt + 1), INTERVAL_MS);
      } catch (err) {
        setTimeout(() => poll(attempt + 1), INTERVAL_MS);
      }
    };
    poll(0);
    return () => { cancelled.current = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-12 py-24 sm:py-32 text-center" data-testid="voucher-success-page">
      {state.phase === "polling" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-terracotta animate-spin" strokeWidth={1.5} />
          <h1 className="mt-6 font-display text-3xl sm:text-4xl text-ink">Zahlung wird geprüft…</h1>
          <p className="mt-4 text-ink-muted">Bitte einen Moment Geduld.</p>
        </>
      )}
      {state.phase === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-sage/15 text-sage flex items-center justify-center mx-auto">
            <Check className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <h1 className="mt-6 font-display text-3xl sm:text-4xl text-ink" data-testid="voucher-paid-title">Vielen Dank für deinen Einkauf!</h1>
          <p className="mt-5 text-ink-muted leading-relaxed">
            Deine Zahlung wurde erfolgreich verarbeitet.<br />
            Der Gutschein wird dir <strong>in den nächsten Tagen per Post</strong> zugestellt.<br />
            Eine Bestätigung mit dem digitalen Gutschein (PDF) ist soeben per E-Mail unterwegs.
          </p>
          {info?.amount_total && (
            <div className="mt-8 inline-block bg-sand-soft border border-border rounded-md px-6 py-4">
              <div className="text-xs uppercase tracking-widest text-ink-muted">Betrag</div>
              <div className="font-display text-2xl text-ink mt-1">CHF {(info.amount_total / 100).toFixed(2)}</div>
            </div>
          )}
          <div className="mt-10">
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-ink/20 rounded-full text-ink hover:bg-ink hover:text-sand transition-colors" data-testid="voucher-paid-home-btn">
              Zur Startseite
            </Link>
          </div>
        </>
      )}
      {(state.phase === "failed" || state.phase === "timeout") && (
        <>
          <div className="w-16 h-16 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <h1 className="mt-6 font-display text-3xl sm:text-4xl text-ink">Zahlungsstatus unklar</h1>
          <p className="mt-5 text-ink-muted leading-relaxed">
            {state.phase === "timeout"
              ? "Die Status-Abfrage hat zu lange gedauert. Falls die Zahlung erfolgreich war, erhältst du in Kürze eine Bestätigung per E-Mail."
              : "Die Zahlung konnte nicht abgeschlossen werden. Du kannst es gerne erneut versuchen."}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/gutscheine" className="px-6 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover">Erneut versuchen</Link>
            <Link to="/" className="px-6 py-3 border border-ink/20 rounded-full text-ink hover:bg-ink hover:text-sand">Zur Startseite</Link>
          </div>
        </>
      )}
    </div>
  );
}
